import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Slider,
  Radio,
  RadioGroup,
  TextField,
  FormControlLabel,
  FormLabel,
  Box,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { DateHistogram } from "./datehistogram";

// "YYYY" or "YYYY-MM" -> { from, to } in ISO-Z
export function bucketToRange(date) {
  const str = String(date);
  if (str.length === 4) {
    return { from: `${str}-01-01T00:00:00Z`, to: `${str}-12-31T23:59:59Z` };
  }
  if (str.length === 7) {
    const [y, m] = str.split("-");
    const lastDay = new Date(y, m, 0).getDate(); // m is 1-based here, so this is correct
    return { from: `${y}-${m}-01T00:00:00Z`, to: `${y}-${m}-${lastDay}T23:59:59Z` };
  }
  return null;
}

function selectedDatesFromRangesParam(rangesParam, buckets) {
  if (!rangesParam) return [];
  const activeFroms = new Set(rangesParam.split(",").map((pair) => pair.split("/")[0]));
  return buckets
    .filter((b) => {
      const r = bucketToRange(b.date);
      return r && activeFroms.has(r.from);
    })
    .map((b) => b.date);
}

export function DateHistogramTool(props) {
  const { query, numFound, facetRestricts, getNavigateUrl, updateHistogramRange } = props;
  const [groupBy, setGroupBy] = useState("year");
  const [range, setRange] = useState([1920, 2025]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState((new Date().getFullYear()).toString());
  const [minSliderValue, setMinSliderValue] = useState(1920);
  const [maxSliderValue, setMaxSliderValue] = useState(2025);
  const [showHistogram, setShowHistogram] = useState(false);
  const [currentQuery, setCurrentQuery] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [childHorizontal, setChildHorizontal] = useState(false);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const resetData = () => {
    setRange([minSliderValue, maxSliderValue]);
    setResetKey((k) => k + 1);
    updateHistogramRange(null);
    navigate(getNavigateUrl(facetParamsString(), ""));
  };

  const facetParamsString = () => {
    const p = new URLSearchParams();
    if (facetRestricts) {
      Object.entries(facetRestricts).forEach(([key, value]) => {
        if (key === "nominal_start_time_ranges") return;
        if (value === null || value === undefined || value === "") return;
        p.append(key, Array.isArray(value) ? value.join(",") : value);
      });
    }
    return p.toString();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { q: query, group_by: groupBy };
      if (groupBy === "month" && year) {
        params.year_of_interest = year;
      }
      // Forward active facet restrictions so the histogram reflects them, but
      // NOT the selected buckets — the histogram should keep showing the full
      // distribution rather than collapse down to the current selection.
      if (facetRestricts) {
        Object.entries(facetRestricts).forEach(([key, value]) => {
          if (key === "nominal_start_time_ranges") return;
          if (value !== null && value !== undefined && value !== "") {
            params[key] = value;
          }
        });
      }

      const res = await axios.get("/heliodata/app/histogram_data", { params });

      const hist = res.data.histogram || [];
      if (hist.length > 0) {
        // set slider range based on min/max years present
        const years = hist.map((h) => parseInt(h.date.slice(0, 4), 10));
        const minY = Math.min(...years);
        const maxY = Math.max(...years);
        setMinSliderValue(parseInt(res.data.histogram[0].date));
        setMaxSliderValue(parseInt(res.data.histogram[res.data.histogram.length - 1].date));
        setRange([minY, maxY]);
      }

      setCurrentQuery(query);
      setRawData(hist);
      setShowHistogram(true);
    } catch (err) {
      // swallow — histogram simply won't render
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (query) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(facetRestricts), query]);

  useEffect(() => {
     if (!searchParams.get("nominal_start_time_ranges")) {
      setRange([minSliderValue, maxSliderValue]);
     }
  }, [searchParams, minSliderValue, maxSliderValue]);


  const handleSelectionChange = (buckets) => {
    const ranges = buckets
      .map((b) => bucketToRange(b.date))
      .filter(Boolean)
      .sort((a, b) => (a.from < b.from ? -1 : 1));

    const histoParams = new URLSearchParams();

    if (ranges.length > 0) {
      histoParams.append(
        "nominal_start_time_ranges",
        ranges.map((r) => `${r.from}/${r.to}`).join(",")
      );
    }

    updateHistogramRange(ranges);
    navigate(getNavigateUrl(facetParamsString(), histoParams.toString()));
  };

  // Just filter buckets by the year range (don't re-aggregate).
  const filteredBuckets = useMemo(() => {
    if (!rawData || rawData.length === 0 || !showHistogram) return [];
    return rawData
      .map((item) => {
        const y = parseInt(String(item.date).slice(0, 4), 10);
        return { ...item, _year: y };
      })
      .filter((item) => item._year >= range[0] && item._year <= range[1])
      .sort((a, b) => {
        const A = a.date;
        const B = b.date;
        return A < B ? -1 : A > B ? 1 : 0;
      })
      .map(({ date, count, items }) => ({ date, count, items }));
  }, [rawData, range, showHistogram]);

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", p: 2 }}>

      <div className="dateHistogramFacetTitle">
        <span>Refine Search</span>

        {loading && (
         <div> <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: "30px",
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2">
              Loading data…
            </Typography>
          </Box></div>
        )}
      </div>

      {showHistogram && numFound !== 0 && (
        <>
          <DateHistogram
            key={resetKey}
            data={filteredBuckets}
            onSelectionChange={handleSelectionChange}
            query={currentQuery}
            onOrientationChange={setChildHorizontal}
            initialSelectedDates={Array.from(new Set([
                ...eraDatesFromBuckets(facetRestricts?.eras, filteredBuckets),
                ...selectedDatesFromRangesParam(searchParams.get("nominal_start_time_ranges"), filteredBuckets),
            ]))}
          />
        </>
      )}

      {showHistogram && numFound !== 0 && !childHorizontal && (
          <div className="datehistogramSliderContainer">
            <Box sx={{ mb: 2 }}>
              {groupBy === "year" && (
                <Slider
                  size="small"
                  value={range}
                  min={minSliderValue}
                  max={maxSliderValue}
                  step={1}
                  onChange={(_, newValue) => setRange(newValue)}
                  valueLabelDisplay="auto"
                />
              )}
            </Box>
          </div>
      )}
    </Box>
  );
}


// decade keys -> predicate over a bucket's "YYYY" / "YYYY-MM" date
export function eraDatesFromBuckets(erasValue, buckets) {
  if (!erasValue) return [];
  const tokens = String(erasValue).split(",").map((s) => s.trim());
  const decades = tokens.filter((d) => /^\d{4}$/.test(d)).map((d) => parseInt(d, 10));
  const hasFuture = tokens.includes("Future");

  const currentDecadeEnd = Math.floor(new Date().getFullYear() / 10) * 10 + 9;

  return buckets
    .filter((b) => {
      const y = parseInt(String(b.date).slice(0, 4), 10);
      if (hasFuture && y > currentDecadeEnd) return true;     // beyond current decade
      return decades.some((dec) => y >= dec && y <= dec + 9); // explicit decades
    })
    .map((b) => b.date);
}
